/// <reference types="vite/client" />

/**
 * API Client Configuration
 *
 * API client that routes requests to the backend server API.
 *
 * Usage:
 *   import { apiClient } from '@/lib/apiClient';
 *   const response = await apiClient.get('/api/animals');
 *   const data = await apiClient.post('/api/users', { body: userData });
 */

import { getClientHeaders } from "./clientMeta.js";

// API base URL from environment (self-hosted Fastify backend).
const API_URL =
  import.meta.env.VITE_REACT_APP_API_URL || "http://localhost:8080";

function getApiUrl(_endpoint: string): string {
  return API_URL;
}

export const API_BASE_URL = API_URL;

type JsonRequestOptions = Omit<RequestInit, "body" | "method"> & {
  body?: unknown;
};

/**
 * Make a GET request to the API
 */
async function get<T = any>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const baseUrl = getApiUrl(endpoint);
  const url = `${baseUrl}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    method: "GET",
    headers: { ...getClientHeaders(), ...options?.headers },
  });
  return response.json();
}

/**
 * Make a POST request to the API
 */
async function post<T = any>(
  endpoint: string,
  options?: JsonRequestOptions,
): Promise<T> {
  const baseUrl = getApiUrl(endpoint);
  const url = `${baseUrl}${endpoint}`;
  const { body, ...restOptions } = options || {};

  const response = await fetch(url, {
    ...restOptions,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getClientHeaders(),
      ...restOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return response.json();
}

/**
 * Make a PUT request to the API
 */
async function put<T = any>(
  endpoint: string,
  options?: JsonRequestOptions,
): Promise<T> {
  const baseUrl = getApiUrl(endpoint);
  const url = `${baseUrl}${endpoint}`;
  const { body, ...restOptions } = options || {};

  const response = await fetch(url, {
    ...restOptions,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getClientHeaders(),
      ...restOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return response.json();
}

/**
 * Make a PATCH request to the API
 */
async function patch<T = any>(
  endpoint: string,
  options?: JsonRequestOptions,
): Promise<T> {
  const baseUrl = getApiUrl(endpoint);
  const url = `${baseUrl}${endpoint}`;
  const { body, ...restOptions } = options || {};

  const response = await fetch(url, {
    ...restOptions,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getClientHeaders(),
      ...restOptions.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return response.json();
}

/**
 * Make a DELETE request to the API
 */
async function del<T = any>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const baseUrl = getApiUrl(endpoint);
  const url = `${baseUrl}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    method: "DELETE",
    headers: { ...getClientHeaders(), ...options?.headers },
  });
  return response.json();
}

// Types for API responses
export interface RemainingSwipesResponse {
  tier: string;
  tierDisplayName: string;
  maxDaily: number | null;
  usedToday: number;
  remainingSwipes: number | null;
  resetsAt: string;
  streak?: {
    current: number;
    longest: number;
    graceAvailable: boolean;
    daysToNextTier: number;
    nextTier: "plus" | null;
  };
}

export interface AppInstallResponse {
  success: boolean;
  upgraded: boolean;
  tier: string;
  tierDisplayName: string;
  maxDaily: number | null;
}

export interface PaymentResponse {
  success: boolean;
  transactionId?: string;
  tier?: string;
  tierDisplayName?: string;
  error?: string;
  message?: string;
}

export type PaymentInterestLevel =
  | "not_willing"
  | "low_interest"
  | "high_interest";

export interface PaymentInterestSurveyResponse {
  success: boolean;
  alreadySubmitted: boolean;
  message?: string;
}

export type FeedbackCategory = "pawmatch" | "features" | "general" | "help";

export interface FeedbackResponse {
  success: boolean;
  message?: string;
}

/**
 * API Client
 *
 * Provides methods for making HTTP requests to the API
 */
export const apiClient = {
  get,
  post,
  put,
  patch,
  delete: del,

  // Payment & Tier methods
  getRemainingSwipes: async (
    authHeaders: Record<string, string>,
  ): Promise<RemainingSwipesResponse> => {
    const baseUrl = getApiUrl("/api/pawmatch/remaining-swipes");
    const url = `${baseUrl}/api/pawmatch/remaining-swipes`;
    const response = await fetch(url, {
      method: "GET",
      headers: authHeaders,
    });
    const data = await response.json();
    if (!response.ok) {
      const error: any = new Error(
        data?.message || "Failed to fetch swipe limits",
      );
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  },

  // Grants the app tier when called from the native shell (the X-Pawseum-Client
  // header, added by `post`, tells the backend it's a native install).
  registerAppInstall: async (
    authHeaders: Record<string, string>,
  ): Promise<AppInstallResponse> =>
    post<AppInstallResponse>("/api/app/install", { headers: authHeaders }),

  processPayment: async (
    tier: "plus" | "pro",
    amount: number,
    authHeaders: Record<string, string>,
  ): Promise<PaymentResponse> => {
    const baseUrl = getApiUrl("/api/billing/payment");
    const url = `${baseUrl}/api/billing/payment`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ tier, amount }),
    });
    return response.json();
  },

  submitPaymentInterestSurvey: async (
    payload: {
      interestLevel: PaymentInterestLevel;
      comments?: string;
    },
    authHeaders: Record<string, string>,
  ): Promise<PaymentInterestSurveyResponse> => {
    const baseUrl = getApiUrl("/api/survey/payment-interest");
    const url = `${baseUrl}/api/survey/payment-interest`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  submitFeedback: async (
    payload: {
      category: FeedbackCategory;
      message: string;
    },
    authHeaders: Record<string, string>,
  ): Promise<FeedbackResponse> => {
    const baseUrl = getApiUrl("/api/feedback");
    const url = `${baseUrl}/api/feedback`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  // Expose the base URL in case it's needed
  baseURL: API_BASE_URL,
};

// Also export the base URL separately for convenience
export { API_BASE_URL as API_URL };
